import React, {useContext, useEffect, useState} from 'react';
import {Button, Input, Checkbox, message, Modal, Upload, Typography} from 'antd';
import {DownloadOutlined, InboxOutlined} from '@ant-design/icons';
import {UploadProps} from 'antd/lib/upload/interface';

import {CheckboxChangeEvent} from 'antd/lib/checkbox';

import GlobalContext from 'context/global_context';
import Icon from 'components/icon';
import {addRoleUserRanges, loadRoleUsersRanges, removeRoleUserRanges, importUsers, downloadTemplate} from 'server/actions/roles';
import {Dictionary, RoleUserRange, SimpleRoleUserRange} from 'types/role_user_range';
import {RoleSettingProps} from 'types/roles';
import {CallBackFunc} from 'types/general';
import {RangeTypes} from 'utils/constants/general';
import {isWeComEnvironment} from 'utils/utils';

import SelectTrigger from 'components/select_trigger/select_trigger';

import MemberItem from './member_item';

const MemberTable: React.FC<RoleSettingProps> = (props) => {
    const {role, memberIds, loadMember, onMemberSelected, onMemberRemove} = props;
    const [memberModalVisible, setMemberModalVisible] = useState<boolean>(false);
    const [batchActive, setBatchActive] = useState<boolean>(false);
    const [checkedAll, setCheckedAll] = useState<boolean>(false);
    const [checkedMemberIds, setCheckedMemberIds] = useState<Set<string>>(new Set());
    const [token, setToken] = useState<string>('');
    const [rolesRanges, setRolesRanges] = useState<Dictionary<Dictionary<RoleUserRange[]>>>({});
    const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
    const [uploadedFile, setUploadedFile] = useState<any>(null);
    const [importError, setImportError] = useState<string>('');

    const {organization, loadOrganizationData, loadStaffsIfNeeded} = useContext(GlobalContext);

    const isMemberNotEmpty = () => {
        return memberIds && memberIds.length > 0;
    };

    useEffect(() => {
        if (!organization) {
            loadOrganizationData();
        }

        if (role && memberIds && memberIds.length) {
            if (!rolesRanges[role.id]) {
                loadRoleUsersRanges(role.id).then(({data, error}) => {
                    if (error) {
                        message.error('获取角色管理范围失败');
                    } else if (data) {
                        const userIds = [];
                        for (const ranges of Object.values(data)) {
                            for (const range of ranges) {
                                if (range.range_type === RangeTypes.Staff) {
                                    userIds.push(range.range_id);
                                }
                            }
                        }

                        if (userIds.length) {
                            loadStaffsIfNeeded(userIds).then();
                        }

                        setRolesRanges((originRolesRanges) => {
                            return {
                                ...originRolesRanges,
                                [role.id]: data,
                            };
                        });
                    }
                });
            }
        }
    }, [role, memberIds]);

    const toggleMemberModalVisible = () => {
        setMemberModalVisible(!memberModalVisible);
    };

    const handleBatchImport = () => {
        setImportModalVisible(true);
    };

    const handleImportModalCancel = () => {
        setImportModalVisible(false);
        setUploadedFile(null);
        setImportError('');
    };

    const handleDownloadTemplate = () => {
        downloadTemplate(role.id).then(({error}) => {
            if (error) {
                message.error('下载模板失败');
            }
        });
    };

    const handleUpload: UploadProps['customRequest'] = async (options) => {
        const {file, onSuccess, onError} = options;
        try {
            // 只保存文件，不执行导入操作
            setUploadedFile(file);
            message.success('文件上传成功');
        } catch (error) {
            message.error('文件上传失败');
            setUploadedFile(null);
        }
    };

    const toggleBatchActive = () => {
        setBatchActive(!batchActive);
        if (batchActive) {
            handleCheckedAll(false);
        }
    };

    const handleFilterToken: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setToken(e.target.value.trim());
    };

    const handleCheckedAll = (checked: boolean) => {
        setCheckedAll(checked);
        if (checked) {
            setCheckedMemberIds(new Set(memberIds));
        } else {
            setCheckedMemberIds(new Set());
        }
    };

    const handleItemChecked = (memberId: string, checked: boolean) => {
        setCheckedMemberIds((originCheckedMemberIds) => {
            const nextCheckedMemberIds = new Set(Array.from(originCheckedMemberIds));
            if (checked) {
                nextCheckedMemberIds.add(memberId);
                if (nextCheckedMemberIds.size === memberIds?.length) {
                    setCheckedAll(true);
                }
            } else {
                nextCheckedMemberIds.delete(memberId);
                setCheckedAll(false);
            }

            return nextCheckedMemberIds;
        });
    };

    const handleMemberRemove = (removeIds: string[], onSuccess?: CallBackFunc) => {
        onMemberRemove(removeIds, () => {
            setRolesRanges((originRolesRanges) => {
                const roleRanges = originRolesRanges[role.id];
                if (!roleRanges) {
                    return originRolesRanges;
                }

                const nextRoleRanges = {
                    ...roleRanges,
                };

                for (const removeId of removeIds) {
                    delete nextRoleRanges[removeId];
                }

                return {
                    ...originRolesRanges,
                    [role.id]: nextRoleRanges,
                };
            });

            if (onSuccess) {
                onSuccess();
            }
        });
    };

    const handleBatchRemove = () => {
        const removeIds = Array.from(checkedMemberIds);
        handleMemberRemove(removeIds, toggleBatchActive);
    };

    const handleRangeRemove = (memberId: string, range: RoleUserRange) => {
        removeRoleUserRanges(role.id, memberId, [range]).then(({error}) => {
            if (error) {
                message.error('删除范围失败');
                return;
            }

            setRolesRanges((originRolesRanges) => {
                const roleRanges = originRolesRanges[role.id];
                if (!roleRanges) {
                    return originRolesRanges;
                }

                const memberRanges = roleRanges[memberId];
                if (!memberRanges) {
                    return originRolesRanges;
                }

                const nextMemberRanges = memberRanges.filter((one) => one.id !== range.id);
                const nextRoleRanges = {
                    ...roleRanges,
                    [memberId]: nextMemberRanges,
                };

                return {
                    ...originRolesRanges,
                    [role.id]: nextRoleRanges,
                };
            });
        });
    };

    const handleRangesAdd = (memberId: string, ranges: SimpleRoleUserRange[]) => {
        addRoleUserRanges(role.id, memberId, ranges).then(({error, data}) => {
            if (error) {
                message.error('添加范围失败');
                return;
            }

            if (data && data.length) {
                setRolesRanges((originRolesRanges) => {
                    const roleRanges = originRolesRanges[role.id];
                    if (!roleRanges) {
                        return originRolesRanges;
                    }

                    const memberRanges = roleRanges[memberId] || [];
                    const nextMemberRanges = [...memberRanges, ...data];
                    const nextRoleRanges = {
                        ...roleRanges,
                        [memberId]: nextMemberRanges,
                    };

                    return {
                        ...originRolesRanges,
                        [role.id]: nextRoleRanges,
                    };
                });
            }
        });
    };

    const renderTop = () => {
        return (
            <div className={'member-table-top mx-16'}>
                <div className={'member-buttons'}>
                    <SelectTrigger
                        holderComponent={
                            <Button
                                type='primary'
                                onClick={toggleMemberModalVisible}
                            >
                                <Icon type='plus'/>
                                {'新增成员'}
                            </Button>
                        }
                        originMemberIds={memberIds}
                        onSelected={onMemberSelected}
                    />
                    {
                        !isWeComEnvironment() ? <Button
                            onClick={handleBatchImport}
                        >
                            {'批量导入'}
                        </Button> : null    
                    }
                    {
                        isMemberNotEmpty() ?
                            <Button
                                onClick={toggleBatchActive}
                            >
                                {batchActive ? '取消批量操作' : '批量操作'}
                            </Button> : null
                    }
                </div>
                {

                    // 20230228微信环境暂时简单处理-隐藏搜索
                    isMemberNotEmpty() && !isWeComEnvironment() ?
                        <div className={'filter'}>
                            <Input
                                prefix={
                                    <Icon
                                        type={'search'}
                                        size={16}
                                        color={'#9E9EA7'}
                                    />
                                }
                                placeholder={'请搜索员工姓名'}
                                allowClear={true}
                                onChange={handleFilterToken}
                            />
                        </div> : null
                }
            </div>
        );
    };

    const renderRows = () => {
        if (!isMemberNotEmpty()) {
            return null;
        }

        return memberIds?.map((memberId) => {
            let ranges;
            const roleRanges = rolesRanges[role.id];
            if (roleRanges) {
                ranges = roleRanges[memberId];
            }

            return (
                <MemberItem
                    key={memberId}
                    memberId={memberId}
                    onMemberRemove={handleMemberRemove}
                    batchActive={batchActive}
                    checkedMemberIds={checkedMemberIds}
                    onItemChecked={handleItemChecked}
                    token={token}
                    ranges={ranges}
                    onRemoveRange={handleRangeRemove}
                    onAddRanges={handleRangesAdd}
                />
            );
        });
    };

    const renderBatch = () => {
        if (!batchActive) {
            return null;
        }

        return (
            <div className={'member-table-batch mx-16'}>
                <div className={'info'}>
                    <span>{'已选择'}<span className={'select-number'}>{checkedMemberIds.size}</span>{'项'}</span>
                    <span
                        className={'cancel-select'}
                        onClick={() => {
                            handleCheckedAll(false);
                        }}
                    >
                        {'取消选择'}
                    </span>
                </div>
                <div className={'member-buttons'}>
                    <Button
                        onClick={toggleBatchActive}
                    >
                        {'取消'}
                    </Button>

                    <Button
                        danger={true}
                        type='primary'
                        disabled={checkedMemberIds.size === 0}
                        onClick={handleBatchRemove}
                    >
                        {'删除'}
                    </Button>
                </div>
            </div>
        );
    };

    const renderImportModal = () => {
        const {Dragger} = Upload;

        const uploadProps: UploadProps = {
            name: 'file',
            multiple: false,
            accept: '.xlsx,.xls',
            showUploadList: false,
            customRequest: handleUpload,
            fileList: uploadedFile ? [{name: uploadedFile.name, size: uploadedFile.size, status: 'done', uid: '1'}] : [],
            onRemove: () => {
                setUploadedFile(null);
            },
        };

        const handleBatchAdd = async () => {
            if (!uploadedFile) {
                message.warning('请先上传文件');
                return;
            }
            try {
                setImportError('');
                const {error, data} = await importUsers(role.id, uploadedFile as File);
                if (error) {
                    message.error('导入失败');
                    return;
                }
                
                // 检查返回的结果是否包含错误信息
                if (data && (data.error_msg || data.failed_num > 0)) {
                    // 显示错误信息
                    const errorMessage = data.error_msg || '';
                    setImportError(errorMessage || '部分数据导入失败');
                    return;
                }
                
                message.success('批量新增成功');
                setImportModalVisible(false);
                setUploadedFile(null);
                setImportError('');
                // 重新加载成员列表
                if (loadMember) {
                    // 清除角色的授权范围数据，这样当 memberIds 变化时，useEffect 会重新加载授权范围数据
                    setRolesRanges(prev => ({...prev, [role.id]: undefined}));
                    loadMember?.();
                }
                
            } catch (error) {
                message.error('导入失败');
            }
        };

        return (
            <Modal
                title="批量新增"
                open={importModalVisible}
                onCancel={handleImportModalCancel}
                footer={null}
                width={500}
            >
                <div style={{padding: '10px 0'}}>
                    {/* 错误信息显示 */}
                    {importError && (
                        <div style={{ 
                            backgroundColor: '#fff2f0', 
                            border: '1px solid #ffccc7', 
                            borderRadius: '4px', 
                            padding: '12px', 
                            marginBottom: '20px',
                            color: '#f5222d'
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                                <span style={{marginRight: '8px'}}>⚠️</span>
                                <span style={{fontWeight: 'bold'}}>导入结果</span>
                            </div>
                            <div style={{marginLeft: '20px'}}>
                                <div dangerouslySetInnerHTML={{__html: importError}} />
                            </div>
                        </div>
                    )}
                    
                    <div style={{textAlign: 'center', marginBottom: '30px'}}>
                        <Dragger {...uploadProps}>
                            {uploadedFile ? (
                                <div style={{textAlign: 'center'}}>
                                    <div style={{fontSize: '48px', color: '#52c41a', marginBottom: '20px'}}>
                                        <InboxOutlined />
                                    </div>
                                    <p style={{marginBottom: '10px'}}>文件上传成功</p>
                                    <p style={{marginBottom: '30px', fontWeight: 'bold'}}>{uploadedFile.name}</p>
                                    <p className="ant-upload-text">点击或拖拽其他文件替换</p>
                                </div>
                            ) : (
                                <div style={{textAlign: 'center'}}>
                                    <div style={{fontSize: '48px', color: '#1890ff', marginBottom: '20px'}}>
                                        <InboxOutlined />
                                    </div>
                                    <p style={{marginBottom: '10px'}}>请上传大小不超过</p>
                                    <p style={{marginBottom: '30px'}}>请上传大小不超过50MB且格式为xls/xlsx的文件</p>
                                    <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                                </div>
                            )}
                        </Dragger>
                    </div>
                    
                    <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
                        <Button onClick={handleDownloadTemplate}>
                            下载模板
                        </Button>
                        <Button type="primary" onClick={handleBatchAdd}>
                            批量新增
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    };

    return (
        <>
            {renderTop()}
            <div className={'member-table-item mx-16 member-table-header'}>
                <div className={'cell-member'}>
                    {
                        batchActive ?
                            <Checkbox
                                className={'checked'}
                                checked={checkedAll}
                                onChange={(e: CheckboxChangeEvent) => handleCheckedAll(e.target.checked)}
                            /> : null
                    }
                    <span>{'成员姓名'}</span>
                </div>
                <div className={'cell-ranges'}>
                    <span>{'授权范围'}</span>
                </div>
                <div className={'cell-operation'}>
                    <span>{'操作'}</span>
                </div>
            </div>
            <div className={'backend-role-setting-scroll'}>
                {renderRows()}
            </div>
            {renderBatch()}
            {renderImportModal()}
        </>
    );
};

export default MemberTable;
